import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { sinceText } from "@/lib/raceFormat";

const clock = (iso) => new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", timeZone: "Australia/Brisbane" }).toLowerCase();

const MarketCoverage = ({ mk }) => (
  <p className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400" data-testid="race-market-coverage">
    <span className={`w-2 h-2 rounded-full ${mk.early ? "bg-amber-400" : "bg-emerald-400"}`} data-testid="race-market-dot" data-state={mk.early ? "early" : "firm"} />
    <span className="text-slate-300">{mk.early ? "Early market" : "Market"}</span>
    {mk.books != null && <span>· {mk.books} bookmaker{mk.books === 1 ? "" : "s"}</span>}
    {(mk.as_of || mk.updated_at) && <span>· prices as of {clock(mk.as_of || mk.updated_at)}</span>}
    {mk.early && <span className="text-slate-500">· can move before the jump</span>}
  </p>
);

export const RaceDataStatus = ({ meeting, race, isAdmin, onRefreshed, finished, now }) => {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  if (meeting.source !== "krok") {
    return <p className="text-[11px] text-slate-500 mt-3" data-testid="race-data-source">{meeting.is_sample ? "Sample data, not real form" : "Imported from a pasted form guide"}</p>;
  }
  const at = race.fetched_at || meeting.fetched_at;
  const stale = at && now - new Date(at).getTime() > 3600000;
  const dot = finished ? "bg-slate-500" : stale ? "bg-amber-400" : "bg-emerald-400";
  const refresh = async () => {
    setBusy(true);
    try { await api("post", `/admin/meetings/${meeting.id}/races/${race.number}/refresh`); toast.success("Race data refreshed from PuntersEdge"); onRefreshed(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 space-y-1">
      <p className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400" data-testid="race-data-source">
        <span className={`w-2 h-2 rounded-full ${dot}`} data-testid="race-data-dot" data-state={finished ? "finished" : stale ? "stale" : "fresh"} />
        Live data · updated <span data-testid="race-fetched-at" className="text-slate-300">{at ? sinceText(at, now) : "—"}</span>
        {isAdmin && (
          <button data-testid="race-refresh-button" onClick={refresh} disabled={busy} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 hover:bg-white/5">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />Refresh
          </button>
        )}
      </p>
      {!finished && race.pe_market?.runners && <MarketCoverage mk={race.pe_market} />}
      {race.fetch_error && (
        <p className="flex items-center gap-1.5 text-[11px] text-rose-300" data-testid="race-fetch-error"><AlertTriangle className="w-3.5 h-3.5" />Last refresh failed: {race.fetch_error}. Showing the earlier data.</p>
      )}
    </div>
  );
};
